import * as React from "react";

import { XIcon, type LucideIconData } from "@lucide/icons";

import { FormInput, type FormInputProps } from "simplydo/react/ui/form";
import { Icon } from "simplydo/react/ui/icon";
import { Popup } from "simplydo/react/ui/popup";

import { Button } from "../../button";
import checkIE from "../lib/checkIE";
import checkMobile from "../lib/checkMobile";
import { invoke } from "../lib/utils";

type FormInputWithRefProps = FormInputProps & {
  clearable?: boolean;
  onClear?: (e: any, data?: any) => void;
  innerRef?: any;
  onMouseEnter?: (e: any, ...args: any[]) => void;
};

class FormInputWithRef extends React.Component<FormInputWithRefProps, any> {
  public render() {
    const {
      value,
      clearable,
      icon,
      onClear,
      innerRef,
      onFocus,
      onBlur,
      onMouseEnter,
      ...rest
    } = this.props;

    const ClearIcon = (
      <Button size="small" variant="outline" onClick={onClear}>
        <Icon icon={XIcon} />
      </Button>
    );

    return (
      <FormInput
        onFocus={onFocus}
        onBlur={onBlur}
        onClick={onFocus as any}
        onMouseEnter={onMouseEnter}
        icon={
          value && clearable ? (
            ClearIcon
          ) : icon && typeof icon === "object" && "node" in icon ? (
            <Icon icon={icon as LucideIconData} />
          ) : icon ? (
            icon
          ) : undefined
        }
        value={value}
        input={{ ref: innerRef }}
        {...(rest as any)}
      />
    );
  }
}

interface InputViewProps {
  /** Used for passing input dom node (input field or inline calendar) to parent component. */
  onMount: (e: HTMLElement) => void;
  /** Called after input field value has changed. */
  onChange: (e: React.SyntheticEvent<HTMLElement>, data: any) => void;
  /** Called when component looses focus. */
  onBlur?: (e: React.SyntheticEvent<HTMLElement>) => void;
  closePopup: () => void;
  openPopup: () => void;
  /** Called on input focus. */
  onFocus?: () => void;
  /** Function for rendering picker. */
  renderPicker: () => React.ReactNode;
  /** Called after clear icon has clicked. */
  onClear?: (e: React.SyntheticEvent<HTMLElement>, data: any) => void;
  /** Whether to close a popup when cursor leaves it. */
  closeOnMouseLeave?: boolean;
  /** A field can have its label next to instead of above it. */
  inlineLabel?: boolean;
  /** Using the clearable setting will let users remove their selection from a calendar. */
  clearable?: boolean;
  /** Optional Icon to display inside the Input. */
  icon?: LucideIconData | boolean;
  /** Icon position. Default: 'right'. */
  iconPosition?: "left" | "right";
  /** Whether popup is closed. */
  popupIsClosed?: boolean;
  /** The node where the picker should mount. */
  mountNode?: HTMLElement;
  /** Input element tabindex. */
  tabIndex?: string | number;
  /** Whether to display inline picker or picker inside a popup. */
  inline?: boolean;
  /** Duration of the CSS transition animation in milliseconds. */
  duration?: number;
  /** Where to display popup. */
  popupPosition?:
    | "top left"
    | "top right"
    | "bottom right"
    | "bottom left"
    | "right center"
    | "left center"
    | "top center"
    | "bottom center";
  /** Currently selected value. */
  value?: string | Date;
  /** Picker width (any value that `style.width` can take). */
  pickerWidth?: string;
  /** Style object for picker. */
  pickerStyle?: object;
  /** Do not display popup if true. */
  readOnly?: boolean;
  /** Try to prevent mobile keyboard appearing. */
  hideMobileKeyboard?: boolean;
  children?: React.ReactNode;
}

class InputView extends React.Component<InputViewProps, any> {
  public static defaultProps = {
    inline: false,
    closeOnMouseLeave: true,
    tabIndex: "0",
    clearable: false,
    duration: 200,
    iconPosition: "right",
  };

  private inputNode: HTMLElement | null | undefined;
  private popupNode: HTMLElement | null | undefined;
  private mouseLeaveTimeout: number | null = null;

  public onBlur = (e: any, ...args: any[]) => {
    const { closePopup } = this.props;

    if (
      e.relatedTarget !== this.popupNode &&
      e.relatedTarget !== this.inputNode &&
      !checkIE()
    ) {
      invoke(this.props, "onBlur", e, ...args);
      closePopup();
    }
  };

  public onMouseLeave = (e: any, ...args: any[]) => {
    const { closeOnMouseLeave, closePopup } = this.props;

    if (
      e.relatedTarget !== this.popupNode &&
      e.relatedTarget !== this.inputNode
    ) {
      if (closeOnMouseLeave) {
        invoke(this.props, "onMouseLeave", e, ...args);
        this.mouseLeaveTimeout = window.setTimeout(() => {
          if (this.mouseLeaveTimeout) {
            closePopup();
          }
        }, 500);
      }
    }
  };

  public onMouseEnter = (e: any, ...args: any[]) => {
    const { closeOnMouseLeave } = this.props;

    invoke(this.props, "onMouseEnter", e, ...args);
    if (
      e.currentTarget === this.popupNode ||
      e.currentTarget === this.inputNode
    ) {
      if (closeOnMouseLeave) {
        clearTimeout(this.mouseLeaveTimeout!);
        this.mouseLeaveTimeout = null;
      }
    }
  };

  public render() {
    const {
      renderPicker,
      popupPosition,
      inline,
      value,
      closeOnMouseLeave,
      onChange,
      onClear,
      children,
      inlineLabel,
      popupIsClosed,
      mountNode,
      tabIndex,
      onMount,
      closePopup,
      openPopup,
      duration,
      pickerWidth,
      pickerStyle,
      iconPosition,
      icon,
      readOnly,
      hideMobileKeyboard,
      ...rest
    } = this.props;

    const inputElement = (
      <FormInputWithRef
        {...(rest as any)}
        // trying to use this hack https://stackoverflow.com/a/7610923 for hiding mobile keyboard
        readOnly={(checkMobile() && hideMobileKeyboard) || readOnly}
        icon={icon as any}
        iconPosition={
          icon && iconPosition !== "right" ? iconPosition : undefined
        }
        innerRef={(e: any) => {
          this.inputNode = e;
          onMount(e);
        }}
        value={value as any}
        tabIndex={tabIndex}
        inline={inlineLabel}
        onClear={(e: any) => (onClear || onChange)(e, { ...rest, value: "" })}
        onFocus={(e: any) => {
          invoke(this.props, "onFocus", e, this.props);
          openPopup();
        }}
        onBlur={this.onBlur}
        onMouseEnter={this.onMouseEnter}
        onChange={onChange}
      />
    );

    if (inline) {
      return renderPicker();
    }

    return (
      <>
        {inputElement}
        {!readOnly && (
          <Popup
            open={!popupIsClosed}
            trigger={null as any}
            position={popupPosition}
            hoverable={closeOnMouseLeave}
            fluid
            transparent
            context={this.inputNode as any}
            on="hover"
            mountNode={mountNode}
          >
            <div
              onBlur={this.onBlur}
              onMouseLeave={this.onMouseLeave}
              onMouseEnter={this.onMouseEnter}
              style={{ outline: "none" }}
              tabIndex={0}
              ref={(ref) => {
                this.popupNode = ref;
              }}
            >
              {renderPicker()}
            </div>
          </Popup>
        )}
      </>
    );
  }

  public scrollListener = () => {
    const { closePopup } = this.props;
    closePopup();
  };

  private setScrollListener() {
    window.addEventListener("scroll", this.scrollListener);
  }

  private unsetScrollListener() {
    window.removeEventListener("scroll", this.scrollListener);
  }
}

export default InputView;
